import { useMemo, type ReactElement } from 'react'
import { ButtonSave, Page } from '@components'
import { useAddRubric, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { notifications } from '@mantine/notifications'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { type TeamId, type UnsavedRubric } from '@terros/common'
import { CopyDefaultButton } from './CopyDefaultButton'
import { PitchScorecardForm } from './PitchScorecardForm'
import { ScorecardFormContext } from './PitchScorecardFormContextType'
import { usePitchScorecardForm } from './usePitchScorecardForm'
import { usePitchScorecardFormContextState } from './usePitchScorecardFormContextState'
import { useQueryState } from 'nuqs'
import { parseAsTeamId } from '@util'

function PitchScorecardAddPage(): ReactElement {
  const { t } = useTranslation()
  const { add, isAdding, error } = useAddRubric()
  const router = useRouter()
  const navigate = useNavigate()
  useToastError(error)
  const [scopeId] = useQueryState<TeamId>('scopeId', parseAsTeamId)
  const form = usePitchScorecardForm({ scopeId: scopeId ?? undefined }, t)
  const contextState = usePitchScorecardFormContextState(form)
  const contextValue = useMemo(() => ({ form, ...contextState }), [form, contextState])

  const handleSubmit = async (values: UnsavedRubric): Promise<void> => {
    await add({ rubric: values })
    router.history.back()
    form.resetDirty(values)
    notifications.show({ message: t('Conversation scorecard saved'), color: 'green' })
  }

  return (
    <ScorecardFormContext.Provider value={contextValue}>
      <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
        <Page>
          <Page.Heading back title={t('Add Conversation Scorecard')}>
            <CopyDefaultButton />
            <ButtonSave disabled={!form.isDirty() || !form.isValid()} loading={isAdding} type='submit' />
          </Page.Heading>
          <Page.Content scrollArea>
            <PitchScorecardForm />
          </Page.Content>
        </Page>
      </form>
    </ScorecardFormContext.Provider>
  )
}

export const Route = createFileRoute('/settings/scorecard_/add/')({
  component: PitchScorecardAddPage,
})
